import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Lock, ShieldAlert } from "lucide-react";
import SupportTickets from "../components/SupportTickets";
import StreamerLiveSupportCard from "../components/StreamerLiveSupportCard";
import StreamerIter3Contracts from "../components/StreamerIter3Contracts";
import Chats from "../components/Chats";
import CreditLedger from "../components/CreditLedger";
import BuyCredits from "../components/BuyCredits";
import AccountDeletionPanel from "../components/AccountDeletionPanel";
import DashboardTabs from "../components/DashboardTabs";

// Iter 138 — Streamer dashboard content grouped into tabs.
// Tabs: Overview · Contracts · Credits · Chats · Support · Settings.
// Gates (email / onboarding / verification / verified-empty) render ABOVE
// the tab bar and gate access to tab content. Top-nav (Dashboard/Marketplace)
// lives in Header.jsx — NOT duplicated here.
export default function StreamerDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [me, setMe] = useState(user);

  const fetchMe = useCallback(async () => {
    try { const { data } = await api.get(`/streamers/${user.id}`); setMe(data); } catch (e) { /* ignore */ }
  }, [user.id]);
  useEffect(() => { fetchMe(); }, [fetchMe]);

  const status = me?.verificationStatus || "not_submitted";
  const emailOk = !!me?.emailVerified;
  const onbComplete = !!me?.streamerOnboardingComplete;
  const verified = !!me?.streamerVerified;

  const gate = !emailOk ? (
    <div data-testid="streamer-gate-email" className="border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-8">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#F5B841] mb-2">{t("streamerGate.email.eyebrow")}</div>
      <h2 className="font-display text-2xl font-bold text-[#E8ECF4] mb-2">{t("streamerGate.email.title")}</h2>
      <p className="text-sm text-[#8B97AE] mb-4">{t("streamerGate.email.body")}</p>
      <Link to="/streamer/onboarding" data-testid="streamer-gate-email-link" className="inline-flex items-center gap-2 border border-[#2DD4A7] text-[#2DD4A7] px-4 py-2 hover:bg-[#2DD4A7] hover:text-[#04150F] transition-colors text-xs font-mono uppercase tracking-widest">{t("streamerGate.email.cta")}</Link>
    </div>
  ) : !onbComplete ? (
    <div data-testid="streamer-gate-onboarding" className="border border-[#2DD4A7]/30 bg-[#2DD4A7]/[0.04] p-8">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7] mb-2">{t("streamerGate.onb.eyebrow")}</div>
      <h2 className="font-display text-2xl font-bold text-[#E8ECF4] mb-2">{t("streamerGate.onb.title")}</h2>
      <p className="text-sm text-[#8B97AE] mb-4">{t("streamerGate.onb.body")}</p>
      {me?.adminNote && (
        <div className="mb-4 border-l-2 border-[#F5B841] bg-[#F5B841]/[0.06] px-3 py-2" data-testid="streamer-gate-admin-note">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">{t("streamerGate.onb.needsMore")}</div>
          <div className="text-sm text-[#E8ECF4]">{me.adminNote}</div>
        </div>
      )}
      <Link to="/streamer/onboarding" data-testid="streamer-gate-onb-link" className="inline-flex items-center gap-2 border border-[#2DD4A7] text-[#2DD4A7] px-4 py-2 hover:bg-[#2DD4A7] hover:text-[#04150F] transition-colors text-xs font-mono uppercase tracking-widest">{t("streamerGate.onb.cta")}</Link>
    </div>
  ) : !verified ? (
    <div data-testid="streamer-locked-panel" className="border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-10 text-center">
      <ShieldAlert className="w-8 h-8 text-[#F5B841] mx-auto mb-3" />
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#F5B841] mb-2">{status === "rejected" ? t("streamerGate.rejected.eyebrow") : t("streamerGate.review.eyebrow")}</div>
      <h2 className="font-display text-3xl font-bold text-[#E8ECF4] mb-3">{status === "rejected" ? t("streamerGate.rejected.title") : t("streamerGate.review.title")}</h2>
      <p className="text-sm text-[#8B97AE] mb-4">{status === "rejected" ? t("streamerGate.rejected.body") : t("streamerGate.review.body")}</p>
      {me?.adminNote && status === "rejected" && (
        <div className="mt-4 mb-2 text-left border-l-2 border-[#F26D6D] bg-[#F26D6D]/[0.06] px-3 py-2 max-w-md mx-auto" data-testid="streamer-locked-admin-note">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D] mb-1">{t("streamerGate.rejected.note")}</div>
          <div className="text-sm text-[#E8ECF4]">{me.adminNote}</div>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#8B97AE] mb-2">{t("streamerDash.eyebrow")}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter" data-testid="streamer-name">{me?.displayName}</h1>
        </div>
      </div>

      {gate}

      {verified && (
        <DashboardTabs
          testidPrefix="streamer-tabs"
          tabs={[
            { id: "overview",  label: t("tabs.streamer.overview"),
              node: <CreditLedger me={me} /> },
            { id: "contracts", label: t("tabs.streamer.contracts"),
              badgeKey: "contracts_change_requested",
              node: <StreamerIter3Contracts /> },
            { id: "credits",   label: t("tabs.streamer.credits"),
              node: <BuyCredits onLedgerChange={fetchMe} /> },
            { id: "chats",     label: t("tabs.streamer.chats"),
              node: <Chats me={user ? { id: user.id, role: "streamer" } : null} /> },
            { id: "support",   label: t("tabs.streamer.support"),
              node: <><StreamerLiveSupportCard /><SupportTickets /></> },
            { id: "settings",  label: t("tabs.streamer.settings"),
              node: <AccountDeletionPanel role="streamer" /> },
          ]}
        />
      )}
      {!verified && emailOk && (
        // Iter 140 — Pending / verification-in-review state must NOT expose the
        // self-service Delete-my-account block. GDPR erasure route stays open
        // via SupportTickets ("Open a ticket"). Verified state still ships
        // AccountDeletionPanel inside the Settings tab (unchanged).
        <div className="space-y-6 mt-8">
          <StreamerLiveSupportCard />
          <SupportTickets />
        </div>
      )}
    </div>
  );
}
